import { useEffect, useRef, useState } from 'preact/hooks'
import type { SearchResult, Suggestion, PaletteView, AskAIResponse, ContentPage } from '../types'
import { WIDGET_CONFIG } from '../config'
import { EmptyState } from './EmptyState'
import { ResultList } from './ResultList'
import { AskAIRow } from './AskAIRow'
import { AnswerView } from './AnswerView'
import { FeedbackView } from './FeedbackView'
import { useClickOutside } from '../hooks/useClickOutside'

interface PaletteProps {
  query: string
  view: PaletteView
  results: SearchResult[]
  suggestions: Suggestion[]
  recentPages?: ContentPage[]
  aiResponse: AskAIResponse | null
  onClose: () => void
  onQueryChange: (query: string) => void
  onSelectSuggestion: (label: string) => void
  onAskAI: (query: string) => void
  onResultClick?: (url: string) => void
  onBack?: () => void
  onFeedbackSubmit?: (text: string) => Promise<void>
  isLoading?: boolean
  aiPending?: boolean
  placeholder?: string
  aiEnabled?: boolean
}

export function Palette({
  query,
  view,
  results,
  suggestions,
  recentPages,
  aiResponse,
  onClose,
  onQueryChange,
  onSelectSuggestion,
  onAskAI,
  onResultClick,
  onBack,
  onFeedbackSubmit,
  isLoading,
  aiPending = false,
  placeholder = WIDGET_CONFIG.copy.searchPlaceholder,
  aiEnabled = true,
}: PaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const paletteRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [copied, setCopied] = useState(false)

  function handleShare() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {})
  }

  // Auto-focus input on open
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Reset selection to first item whenever results change
  useEffect(() => {
    setActiveIndex(0)
  }, [results])

  useClickOutside(paletteRef, onClose, true)

  function handleKeyDown(e: KeyboardEvent) {
    if (e.isComposing) return

    if (view === 'results') {
      if (e.key === 'ArrowDown' && results.length > 0) {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, results.length - 1))
        return
      }
      if (e.key === 'ArrowUp' && results.length > 0) {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        if (results.length === 0 && query.trim()) {
          onAskAI(query)
        } else {
          const active = results[activeIndex]
          if (active) {
            onResultClick?.(active.url)
            window.location.href = active.url
          }
        }
        return
      }
    }

    if (e.key === 'Enter' && view === 'empty' && query.trim()) {
      onAskAI(query)
    }
  }

  const showAskAIRow = aiEnabled && !isLoading && !aiPending &&
    (view === 'empty' || (view === 'results' && results.length > 0))
  const isFeedback = view === 'feedback'

  return (
    <div class="spotlight-backdrop" aria-modal="true" role="dialog">
      <div class="spotlight-palette" ref={paletteRef}>
        {/* Search input — hidden on feedback page */}
        {!isFeedback && (
          <div class="spotlight-input-row">
            <svg class="spotlight-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              class="spotlight-input"
              type="text"
              placeholder={placeholder}
              value={query}
              onInput={(e) => onQueryChange((e.target as HTMLInputElement).value)}
              onKeyDown={handleKeyDown}
              aria-label="Search"
              autocomplete="off"
              spellcheck={false}
            />
            <span class="spotlight-esc-hint">{WIDGET_CONFIG.copy.escHint}</span>
          </div>
        )}

        {/* Results area */}
        {isFeedback ? (
          <FeedbackView
            onBack={onBack ?? (() => {})}
            onSubmit={onFeedbackSubmit ?? (() => Promise.resolve())}
          />
        ) : isLoading ? (
          <div class="spotlight-results" style="padding: 24px 16px; text-align: center; color: var(--spotlight-text-muted); font-size: 14px;">
            {WIDGET_CONFIG.copy.loading}
          </div>
        ) : (
          <>
            {view === 'empty' && (
              <EmptyState
                suggestions={suggestions}
                recentPages={recentPages}
                onSelectSuggestion={onSelectSuggestion}
              />
            )}
            {view === 'results' && (
              aiPending
                ? (
                  <div class="spotlight-ai-searching">
                    <div class="spotlight-ai-searching-icon">{WIDGET_CONFIG.copy.answerIcon}</div>
                    <div class="spotlight-ai-searching-text">Searching with AI…</div>
                  </div>
                )
                : <ResultList results={results} query={query} activeIndex={activeIndex} onResultClick={onResultClick} />
            )}
            {view === 'asking' && (
              <div class="spotlight-ai-searching">
                <div class="spotlight-ai-searching-icon">{WIDGET_CONFIG.copy.answerIcon}</div>
                <div class="spotlight-ai-searching-text">{WIDGET_CONFIG.copy.thinking}</div>
              </div>
            )}
            {view === 'answer' && aiResponse && (
              <AnswerView response={aiResponse} />
            )}
            {!aiPending && (view === 'empty' || (view === 'results' && results.length > 0)) && (
              <div class="spotlight-actions-strip">
                <div class="spotlight-action-chip" onClick={handleShare} role="button">
                  <span class="spotlight-action-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                  </span>
                  <span class="spotlight-action-label">{copied ? WIDGET_CONFIG.copy.shareCopied : WIDGET_CONFIG.copy.shareLabel}</span>
                  <span class="spotlight-suggestion-arrow">{copied ? '✓' : '›'}</span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Ask AI row — only shown when not already in AI mode */}
        {showAskAIRow && (
          <AskAIRow query={query || undefined} onAskAI={onAskAI} />
        )}

        {/* Footer */}
        <div class="spotlight-footer">
          {WIDGET_CONFIG.copy.footer}
        </div>
      </div>
    </div>
  )
}
